// Tenant facility (work-order) numbering config, loaded ONCE at the app shell
// (Layout) and shared via context so every work-order surface can format
// `#number` consistently without each one re-fetching. The raw integer is what
// the API stores; this only carries the display prefix + zero-padding width.
// Mirrors ticketNumbering.tsx. Falls back to the legacy bare-integer format
// until (or if) the config loads.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getFacilityNumbering } from "./api";
import { DEFAULT_TICKET_NUMBER_FORMAT, type TicketNumberFormat } from "./format";

const FacilityNumberingContext = createContext<TicketNumberFormat>(DEFAULT_TICKET_NUMBER_FORMAT);

export function FacilityNumberingProvider({ children }: { children: ReactNode }) {
  const [cfg, setCfg] = useState<TicketNumberFormat>(DEFAULT_TICKET_NUMBER_FORMAT);

  useEffect(() => {
    const ctrl = new AbortController();
    getFacilityNumbering(ctrl.signal)
      .then((res) => setCfg({ prefix: res.numbering.prefix, padding: res.numbering.padding }))
      .catch(() => {
        /* transient failure → keep the safe bare-integer default */
      });
    return () => ctrl.abort();
  }, []);

  return <FacilityNumberingContext.Provider value={cfg}>{children}</FacilityNumberingContext.Provider>;
}

/** The current tenant's work-order-number display config (prefix + padding). */
export function useFacilityNumberFormat(): TicketNumberFormat {
  return useContext(FacilityNumberingContext);
}
